/** As peças de lista que as telas do DP repetem: a barra única de filtros e ações, a
 *  informação sempre com o ícone na frente, a pessoa na primeira coluna e a última
 *  tratativa. Os filtros moram na URL, em `filtros.ts`.
 *
 *  O padrão das telas do DP, desde 27/10/2026: os indicadores no topo, e logo abaixo uma
 *  linha só com os filtros à esquerda e as ações, como Novo, Importar e Excel, à
 *  direita. Toda informação de célula e de detalhe leva um ícone antes do valor, e a
 *  explicação de como a tela funciona mora no ícone de ajuda, não num aviso fixo. */
import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { BriefcaseBusiness, CircleHelp, History, IdCard, Search, type LucideIcon } from 'lucide-react'
import { fmtDateTimeShort } from '../../../shared/format'
import { useI18n } from '@/core/i18n/useI18n'
import type { Tratativa } from './tipos'

export function BarraDeFiltros({
  busca, onBusca, filial, onFilial, filiais, children, acoes, antes, semBusca = false, placeholder,
}: {
  busca: string
  onBusca: (v: string) => void
  /** Sem `onFilial`, a barra não mostra a filial, como em Multas, que não tem. */
  filial?: string
  onFilial?: (v: string) => void
  filiais?: { filial: string; filial_nome: string | null }[]
  /** Filtros a mais, depois da filial. */
  children?: ReactNode
  /** O que vem antes da busca, como o mês ou a competência. */
  antes?: ReactNode
  /** As ações da tela, à direita na mesma linha. */
  acoes?: ReactNode
  /** Esconde a busca e a filial, como na competência que ainda não foi aberta. */
  semBusca?: boolean
  /** O texto da busca, quando ela não procura pessoa. */
  placeholder?: string
}) {
  const { t } = useI18n()
  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
      {antes}
      {!semBusca && (
        <>
          <TextField size="small" value={busca} onChange={(e) => onBusca(e.target.value)}
            placeholder={placeholder ?? t('dp.busca')} sx={{ flex: '1 1 160px', minWidth: 150, maxWidth: 320 }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search size={16} /></InputAdornment> } }} />
          {onFilial && (
            <TextField select size="small" value={filial ?? ''} onChange={(e) => onFilial(e.target.value)}
              sx={{ minWidth: 140 }} label={t('dp.filial')}>
              <MenuItem value="">{t('dp.todasFiliais')}</MenuItem>
              {filiais?.map((f) => <MenuItem key={f.filial} value={f.filial}>{f.filial_nome ?? f.filial}</MenuItem>)}
            </TextField>
          )}
          {children}
        </>
      )}
      {acoes && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', ml: 'auto' }}>{acoes}</Box>
      )}
    </Box>
  )
}

/** Uma informação com o ícone na frente: o padrão de toda variável nas telas do DP.
 *  `semQuebra` é para data e nome curto, que não podem partir em duas linhas; `dica`
 *  diz no hover o que o ícone quer dizer, quando o rótulo não aparece. */
export function Info({ icone: Icone, children, cor = 'text.secondary', forte = false, tamanho = 12, semQuebra = false, dica }: {
  icone: LucideIcon
  children: ReactNode
  cor?: string
  forte?: boolean
  tamanho?: number
  semQuebra?: boolean
  dica?: string
}) {
  // Sem `minWidth: 0` e com break-word, e não anywhere: os dois zeram a largura mínima
  // da informação dentro da grade da célula, e a tabela espreme a coluna até partir
  // "Paracatu" em "Paracat u". Assim a coluna nunca fica mais estreita que a palavra.
  const corpo = (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75, color: cor, fontSize: tamanho,
      fontWeight: forte ? 600 : 400, lineHeight: 1.35,
    }}>
      <Box component="span" sx={{ display: 'inline-flex', flex: 'none', opacity: 0.8 }}><Icone size={tamanho + 2} /></Box>
      <Box component="span" sx={{ overflowWrap: 'break-word', whiteSpace: semQuebra ? 'nowrap' : undefined }}>{children}</Box>
    </Box>
  )
  return dica ? <Tooltip title={dica} describeChild>{corpo}</Tooltip> : corpo
}

/** O como funciona da tela num ícone, no lugar do aviso fixo acima da lista. */
export function Ajuda({ texto }: { texto: string }) {
  const { t } = useI18n()
  return (
    <Tooltip title={texto} describeChild>
      <IconButton size="small" aria-label={t('dp.comoFunciona')} sx={{ color: 'text.secondary' }}>
        <CircleHelp size={18} />
      </IconButton>
    </Tooltip>
  )
}

/** As ações de uma linha da lista, sempre numa linha só. O botão só de ícone perde a
 *  largura mínima de 64px do MUI, que sozinha empurrava a tabela para a rolagem. */
export function AcoesDaLinha({ children }: { children: ReactNode }) {
  return (
    <Box sx={{
      display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'nowrap', whiteSpace: 'nowrap',
      '& .MuiButton-root': { minWidth: 0 },
      '& .MuiButton-root:not(:has(.MuiButton-startIcon))': { px: 1 },
    }}>
      {children}
    </Box>
  )
}

export function CelulaPessoa({ nome, filial, matricula, funcao, extra }: {
  nome: string
  filial: string | null
  matricula: string | null
  funcao?: string | null
  extra?: ReactNode
}) {
  const { t } = useI18n()
  return (
    <Box sx={{ display: 'grid', gap: 0.25, minWidth: 140, lineHeight: 1.3 }}>
      <Box component="span" sx={{ fontWeight: 600, overflowWrap: 'break-word' }}>{nome}</Box>
      <Info icone={IdCard} semQuebra>{matricula ? `${filial}-${matricula}` : t('dp.semMatricula')}</Info>
      {funcao && <Info icone={BriefcaseBusiness}>{funcao}</Info>}
      {extra}
    </Box>
  )
}

export function CelulaTratativa({ tratativa }: { tratativa: Tratativa | null }) {
  const { t } = useI18n()
  if (!tratativa) return null
  return (
    <Tooltip title={tratativa.texto}>
      <Box sx={{ display: 'grid', gap: 0.25 }}>
        <Info icone={History} cor="text.primary">{t(`dp.tratativas.tipo.${tratativa.tipo}`)}</Info>
        <Box component="span" sx={{ fontSize: 12, color: 'text.secondary', pl: 2.6 }}>{fmtDateTimeShort(tratativa.changed_at)}</Box>
      </Box>
    </Tooltip>
  )
}
